import { useTranslate } from '@pankod/refine-core'
import dayjs from 'dayjs'

import {
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  FormProps,
  Input,
  Row,
  Select,
  Spin
} from '@pankod/refine-antd'
import { definitions, Supplement } from 'interfaces'
import { OrderSimpleInfo } from 'pages/orders'
import { useEffect, useMemo, useState } from 'react'
import { IssueLocationId, IssueTypeIdTypes, SubIssueTypeIdTypes } from 'types/types'
import { useDebouncedCallback } from 'use-debounce'
import { findAdminUsersByText, findOrdersByIdWithoutIssues, getOrderItems, ISSUE_SUB_TYPE_OTHER_ID, ISSUE_SUB_TYPE_REFUND_ID, ISSUE_TYPE_REFUND_CANARY_ID, ISSUE_TYPE_REFUND_MADRID_ID, RETURN_ISSUE_METHOD_CORREOS, RETURN_ISSUE_METHOD_DELIVERY, SENT_SUPPLIER_ID, sortAlphabetically, supabaseClient } from 'utility'
import { getConfigForClientStatusType, getConfigForIssueType, ISSUE_LOCATION_MAPPER, ISSUE_TYPES_MAPPER } from 'utility/mapper'

type IssueFormProps = {
  formProps: FormProps<{}>
  isCreate: boolean
  issueTypeId?: number
  orderId?: number
  itemIds?: number[]
  setUserGmail: any
  setNote: any
  setCanSave: (canSave: boolean) => void
}

export const IssueForm: React.FC<IssueFormProps> = ({
  formProps,
  isCreate,
  issueTypeId,
  orderId,
  itemIds,
  setUserGmail,
  setNote,
  setCanSave
}) => {
  const t = useTranslate()
  const [selectedIssueTypeId, setSelectedIssueTypeId] = useState<IssueTypeIdTypes>(formProps.initialValues?.issue_type_id)
  const [selectedSubIssueTypeId, setSelectedSubIssueTypeId] = useState<SubIssueTypeIdTypes>(formProps.initialValues?.issue_sub_type_id)
  const [location, setLocation] = useState<string>(formProps.initialValues?.location)
  const [completed, setCompleted] = useState<boolean>(formProps.initialValues?.is_completed)
  const [wallapop, setWallapop] = useState<boolean>(formProps.initialValues?.is_wallapop)
  const [orders, setOrders] = useState<definitions['Order'][]>([])
  const [selectedOrder, setSelectedOrder] = useState<definitions['Order']>()
  const [itemsOptions, setItemsOptions] = useState<definitions['items'][]>([])
  const [fetching, setFetching] = useState(false)
  const [userOptions, setUserOptions] = useState<definitions['users'][]>([])
  const [returnType, setReturnType] = useState<string>(formProps.initialValues?.return_method || null)
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>(itemIds || [])
  const debouncedSearchOrders = useDebouncedCallback((value) => handleSearchOrders(value), 500)

  // eslint-disable-next-line no-unused-vars
  const [pickupSlot, setPickupSlot] = useState<string>(formProps.initialValues?.home_pickup_data?.schedule)
  // eslint-disable-next-line no-unused-vars
  const [pickupDay, setPickupDay] = useState<string | undefined>(formProps.initialValues?.home_pickup_data?.date)
  // eslint-disable-next-line no-unused-vars
  const [observation, setObservations] = useState<string>(formProps.initialValues?.home_pickup_data?.observations)
  const [sendSup, setSendSup] = useState<boolean>(true)
  const [homePickupSupData, setHomePickupSupData] = useState<Supplement[]>([])

  async function handleSearchOrders (newValue: string) {
    if (newValue.length > 0) {
      setFetching(true)
      const res = await findOrdersByIdWithoutIssues(newValue)
      if (res) {
        setOrders(res || [])
      }
      setFetching(false)
    }
  }

  const handleSearchUser = async (newValue: string) => {
    if (newValue.length > 3) {
      setFetching(true)
      const res = await findAdminUsersByText(newValue)
      if (res) {
        setUserOptions(res)
      }
      setFetching(false)
    }
  }

  async function handleSearchItems (orderId: number) {
    setFetching(true)
    const res = await getOrderItems(orderId)
    if (res) {
      setItemsOptions(res || [])
    }
    setFetching(false)
  }

  async function checkHomePickupSupData (orderId: number) {
    const { data: existingSupplement } = await supabaseClient
      .from('supplements')
      .select('id')
      .eq('order_id', orderId)
      .eq('description', 'home_pickup')
      .eq('deleted', false)

    setHomePickupSupData(existingSupplement || [])
    if (existingSupplement && existingSupplement.length > 0) {
      setSendSup(false)
    }
  }

  useEffect(() => {
    if (!isCreate && orderId) {
      handleSearchItems(orderId)
      checkHomePickupSupData(orderId)
    }
  }, [])

  useEffect(() => {
    formProps.form?.setFieldsValue({ item_ids: itemIds })
    setSelectedItemIds(itemIds || [])
  }, [itemIds])

  useEffect(() => {
    if (isCreate) return

    const requiresItems = !isCreate && Number(selectedIssueTypeId) !== ISSUE_TYPE_REFUND_MADRID_ID && Number(selectedSubIssueTypeId) !== ISSUE_SUB_TYPE_OTHER_ID
    const requiresPickupData = returnType === RETURN_ISSUE_METHOD_DELIVERY
    const hasRequiredItems = !requiresItems || selectedItemIds.length > 0
    const hasRequiredPickupData = !requiresPickupData || Boolean(pickupSlot && pickupDay)

    setCanSave(hasRequiredItems && hasRequiredPickupData)
    console.log({ canSave: hasRequiredItems && hasRequiredPickupData, requiresItems, requiresPickupData, hasRequiredItems, hasRequiredPickupData })
  }, [isCreate, pickupDay, pickupSlot, returnType, selectedItemIds, selectedSubIssueTypeId, selectedIssueTypeId])

  useEffect(() => {
    const locationVal: IssueLocationId = formProps.initialValues?.location
    formProps.form?.setFieldsValue({
      client_status_id: formProps.initialValues?.client_status_id || undefined,
      issue_status_id: formProps.initialValues?.issue_status_id || undefined,
      location: formProps.initialValues?.location || undefined,
      user_can_show: selectedIssueTypeId === ISSUE_TYPE_REFUND_CANARY_ID,
      sentSup: sendSup
    })
    setLocation(`${locationVal}`)
  }, [formProps])

  const { issueSubTypes } = getConfigForIssueType(selectedIssueTypeId || issueTypeId)
  const { clientStatuses, issueStatuses } = getConfigForClientStatusType(selectedSubIssueTypeId || issueTypeId)

  // Compute available pickup days (next 2 business days: Monday–Friday)
  const availablePickupDays = useMemo(() => {
    const days: { value: string; label: string }[] = []
    const today = new Date()
    let i = 1
    while (days.length < 2) {
      const next = new Date(today)
      next.setDate(today.getDate() + i)
      const dayOfWeek = next.getDay()
      // 0=Sunday, 6=Saturday
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        const dayNum = next.getDate()
        const monthNum = next.getMonth() + 1
        const weekdayName = next.toLocaleDateString('es-ES', { weekday: 'long' })
        days.push({
          value: next.toISOString().split('T')[0],
          label: `${dayNum}/${monthNum} (${weekdayName})`
        })
      }
      i++
    }
    return days
  }, [])

  return (
    <>
      <Form
        {...formProps}
        layout="vertical"
        initialValues={{
          issue_type_id: issueTypeId,
          order_id: orderId,
          item_ids: itemIds,
          sentSup: sendSup,
          ...formProps.initialValues
        }}
      >
        <Form.Item
          label={t('issues.fields.issue_type_id')}
          name='issue_type_id'
          hidden={Boolean(isCreate && issueTypeId)}
          rules={[{ required: true }]}
        >
          <Select
            options={Object.entries(ISSUE_TYPES_MAPPER).map(([key, value]) => ({
              value: parseInt(key),
              label: t(`issues.issue_types.${value}`)
            }))}
            onChange={(value) => {
              setSelectedIssueTypeId(value)
              formProps.form?.setFieldsValue({
                issue_sub_type_id: undefined,
                client_status_id: undefined,
                issue_status_id: undefined,
                user_can_show: value === ISSUE_TYPE_REFUND_CANARY_ID
              })
            }}
          />
        </Form.Item>
        <Row gutter={[64, 0]} wrap>
          <Col xs={24} xl={isCreate ? 24 : 14}>
            <Form.Item
              label={t('issues.fields.order_id')}
              name="order_id"
              hidden={Boolean(!isCreate || orderId)}
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                defaultActiveFirstOption={false}
                showArrow={true}
                onSearch={debouncedSearchOrders}
                options={(orders || []).map(order => ({
                  value: order.id,
                  label: order.id
                }))}
                notFoundContent={fetching ? <Spin size="small" /> : null}
                filterOption={false}
                onChange={(value) => setSelectedOrder(orders.find(order => order.id === value))}
              />
            </Form.Item>
            <Form.Item
              label={t('issues.fields.issue_sub_type_id')}
              name="issue_sub_type_id"
            >
              <Select
                options={Object.entries(issueSubTypes || [])
                  .map(([key, value]) => ({
                    value: parseInt(key),
                    label: t(`issues.issue_sub_types.${value}`)
                  }))
                  .sort((a, b) => sortAlphabetically(a.label, b.label))
                }
                onChange={(value) => {
                  formProps.form?.setFieldsValue({
                    client_status_id: undefined,
                    issue_status_id: undefined
                  })
                  setSelectedSubIssueTypeId(value)
                }}
              />
            </Form.Item>
            {!isCreate && (
              <>
                <Form.Item
                  label={t('issues.fields.item_ids')}
                  name="item_ids"
                  rules={[{ required: Number(selectedIssueTypeId) !== ISSUE_TYPE_REFUND_MADRID_ID && Number(selectedSubIssueTypeId) !== ISSUE_SUB_TYPE_OTHER_ID }]}
                >
                  <Select
                    mode='multiple'
                    options={(itemsOptions || []).map(item => ({
                      value: item.id,
                      label: item.name
                    }))}
                    onChange={(value) => {
                      setSelectedItemIds(value || [])
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label={t('issues.fields.client_status_id')}
                  name="client_status_id"
                >
                  <Select
                    disabled={!clientStatuses}
                    options={clientStatuses
                      ? Object.entries(clientStatuses)
                        .map(([key, value]) => ({
                          value: parseInt(key),
                          label: t(`issues.client_statuses.${value}`)
                        }))
                        .sort((a, b) => sortAlphabetically(a.label, b.label))
                      : []
                    }
                  />
                </Form.Item>
                <Form.Item
                  label={t('issues.fields.issue_status_id')}
                  name="issue_status_id"
                >
                  <Select
                    disabled={!issueStatuses}
                    options={issueStatuses
                      ? Object.entries(issueStatuses)
                        .map(([key, value]) => ({
                          value: parseInt(key),
                          label: t(`issues.issue_statuses.${value}`)
                        }))
                        .sort((a, b) => sortAlphabetically(a.label, b.label))
                      : []
                    }
                    onChange={(value) => {
                      if (value === 4) {
                        formProps.form?.setFieldsValue({
                          is_opened: false,
                          location: SENT_SUPPLIER_ID
                        })
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item
                  label={t('issues.fields.location')}
                  name="location"
                >
                  <Select
                    key={location}
                    style={{ width: 180 }}
                    options={Object.entries(ISSUE_LOCATION_MAPPER).map(([key, value]) => ({
                      value: parseInt(key),
                      label: t(`issues.issue_location.${value}`)
                    }))}
                    onChange={(value) => {
                      formProps.form?.setFieldsValue({
                        location: value
                      })
                      setLocation(value)
                    }}
                    defaultValue={location}
                  />
                </Form.Item>
              </>
            )}
            <Form.Item
              label={t('issues.fields.is_completed')}
              name="is_completed"
            >
              <Checkbox defaultChecked={completed} onChange={(e: any) => {
                const newVal = !completed
                setCompleted(newVal)
                formProps.form?.setFieldsValue({ is_completed: newVal })
              }} />
            </Form.Item>
            <Form.Item
              label={t('issues.fields.is_wallapop')}
              name="is_wallapop"
            >
              <Checkbox defaultChecked={wallapop} onChange={(e: any) => {
                const newVal = !wallapop
                setWallapop(newVal)
                formProps.form?.setFieldsValue({ is_wallapop: newVal })
              }} />
            </Form.Item>
          </Col>
          {!isCreate && (
            <>
              <Col xs={24} xl={8}>
                <Form.Item
                  label={t('issues.fields.refund_method')}
                  name="shipping_method"
                >
                  <Select
                    options={[
                      { label: t('refund_methods.pickup'), value: 'Recogida' },
                      { label: t('refund_methods.delivery'), value: 'Entrega' }
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  label={t('issues.fields.shipper')}
                  name="shipper"
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label={t('issues.fields.tracking_id')}
                  name="tracking_id"
                >
                  <Input />
                </Form.Item>
                {Number(selectedSubIssueTypeId) === ISSUE_SUB_TYPE_REFUND_ID && (
                  <>
                    <Form.Item
                      label={t('issues.fields.return_method')}
                      name="return_method"
                    >
                      <Select
                        defaultValue={returnType}
                        options={[
                          { label: RETURN_ISSUE_METHOD_CORREOS, value: RETURN_ISSUE_METHOD_CORREOS },
                          { label: RETURN_ISSUE_METHOD_DELIVERY, value: RETURN_ISSUE_METHOD_DELIVERY }
                        ]}
                        onChange={(value: string) => {
                          formProps.form?.setFieldsValue({
                            refund_method: value
                          })
                          setReturnType(value)
                        }}
                      />
                    </Form.Item>
                    {(returnType === RETURN_ISSUE_METHOD_DELIVERY) && (
                      <>
                        <Form.Item
                          label={t('refund_methods.home_pickup_data.schedule')}
                          name="pickup_schedule"
                          rules={[{ required: (returnType === RETURN_ISSUE_METHOD_DELIVERY) }]}
                        >
                          <Select
                            defaultValue={pickupSlot}
                            options={[
                              { label: t('refund_methods.home_pickup_data.schedule_morning'), value: 'morning' },
                              { label: t('refund_methods.home_pickup_data.schedule_afternoon'), value: 'afternoon' }
                            ]}
                            onChange={(value) => setPickupSlot(value)}
                          />
                        </Form.Item>
                        <Form.Item
                          label={t('refund_methods.home_pickup_data.date_pickup')}
                          name="pickup_date"
                          rules={[{ required: (returnType === RETURN_ISSUE_METHOD_DELIVERY) }]}
                        >
                          <DatePicker
                            format="YYYY-MM-DD"
                            disabledDate={(current) => !availablePickupDays.some((day) => day.value === current.format('YYYY-MM-DD'))}
                            defaultValue={pickupDay ? dayjs(pickupDay) : undefined}
                            onChange={(value) => setPickupDay(value?.format('YYYY-MM-DD'))}
                          />
                        </Form.Item>
                        <Form.Item
                          label={t('refund_methods.home_pickup_data.observations')}
                          name="observations"
                        >
                          <Input defaultValue={observation} />
                        </Form.Item>
                        <Form.Item
                          label={t('issues.fields.home_pickup.sent_sup')}
                          name="sentSup"
                          valuePropName="checked"
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                            <Checkbox checked={sendSup} onChange={(e) => {
                              setSendSup(e.target.checked)
                              formProps.form?.setFieldsValue({ sentSup: e.target.checked })
                            }} />
                            <p>{t(`issues.fields.home_pickup.${sendSup ? '' : 'not_'}sent_sup_info`)}</p>
                          </div>
                          {homePickupSupData && homePickupSupData.length > 0 && (
                            <b style={{ color: 'red' }}>{t('issues.fields.home_pickup.home_pickup_sup_exists')}</b>
                          )}
                        </Form.Item>
                      </>
                    )}
                  </>
                )}
                <Form.Item
                  label={t('issues.fields.issue_status')}
                  name="is_opened"
                >
                  <Select
                    options={[
                      { label: t('issues.issue_status.open'), value: true },
                      { label: t('issues.issue_status.close'), value: false }
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  label={t('issues.fields.asigned_to')}
                  name="asigned_to"
                >
                  <Select
                    showSearch
                    defaultActiveFirstOption={false}
                    showArrow={true}
                    onSearch={handleSearchUser}
                    onChange={(value) => setUserGmail(value)}
                    options={(userOptions || []).map(u => ({
                      value: u.email
                    }))}
                    notFoundContent={fetching ? <Spin size="small" /> : null}
                    filterOption={false}
                  />
                </Form.Item>
              </Col>
              <Divider />
              <Col xs={24} sm={8}>
                <Form.Item
                  label={t('issues.fields.request_date')}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : ''
                  })}
                  name="request_date"
                >
                  <DatePicker />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  label={t('issues.fields.issue_deadline')}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : ''
                  })}
                  name="issue_deadline"
                >
                  <DatePicker />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                {/* <Form.Item
                  label={t('issues.fields.delivery_deadline')}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : ''
                  })}
                  name="delivery_deadline"
                >
                  <DatePicker />
                </Form.Item> */}
              </Col>
              <Divider />
              <Col xs={24}>
                <Form.Item
                  label={t('issues.fields.reason')}
                  name="reason"
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label={t('issues.fields.notes')}
                >
                  <Input.TextArea onChange={(e) => { setNote(e.target.value) }} />
                </Form.Item>
                <Form.Item
                  label={t('issues.fields.notes_marketplace')}
                  name="notes_marketplace"
                >
                  <Input.TextArea />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </Form>
      {isCreate && issueTypeId && <OrderSimpleInfo order={selectedOrder} />}
    </>
  )
}
